<template>
  <section v-if="item" class="news-view">
    <div class="news-cover" v-if="coverUrl">
      <img :src="coverUrl" alt="news" />
    </div>

    <div class="news-meta">
      <div>{{ formattedDate }}</div>
      <button type="button" class="share-btn" @click="shareNews">{{ t('share') }}</button>
    </div>

    <div class="news-content">
      <h3 class="title">{{ titleText }}</h3>
      <p class="description" v-html="bodyText"></p>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { useFeedNewsDetailQuery } from '@/queries';
import { useTopbarStore } from '@/stores/topbar';
import { getLangShort, parseMaybeJson } from '@/utils/feed';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const topbarStore = useTopbarStore();
const lang = getLangShort();

const newsId = computed(() => route.params.id);
const newsDetailQuery = useFeedNewsDetailQuery(newsId);
const item = computed(() => newsDetailQuery.data.value || null);

const titleText = computed(() => {
  const titleByLang = parseMaybeJson(item.value?.title_by_lang, {});
  return titleByLang?.[lang] || item.value?.caption || '';
});

const bodyText = computed(() => {
  const textByLang = parseMaybeJson(item.value?.text_by_langs, {});
  return textByLang?.[lang] || item.value?.text || '';
});

const coverUrl = computed(() => {
  const coverByLang = parseMaybeJson(item.value?.cover_by_langs, {});
  return coverByLang?.[lang] || item.value?.image || '';
});

const formattedDate = computed(() => {
  const date = item.value?.date || item.value?.published_at;
  if (!date) return '';
  return new Intl.DateTimeFormat(lang === 'kk' ? 'kk-KZ' : 'ru-RU', {
    day: 'numeric',
    month: 'long'
  }).format(new Date(date));
});

async function shareNews() {
  const url = `${window.location.origin}/feed/${item.value?.id}`;
  if (navigator.share) {
    await navigator.share({
      title: t('feed'),
      text: item.value?.caption || '',
      url
    });
    return;
  }
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(url);
  }
}

onMounted(() => {
  topbarStore.setTitle(t('feed'));
  topbarStore.addButton({
    type: 'left',
    icon: 'keyboard_backspace',
    action: () => router.push('/feed')
  });
});
</script>

<style scoped>
.news-cover {
  display: flex;
  justify-content: center;
}

.news-cover img {
  max-width: 700px;
  width: 100%;
  border-radius: 8px;
}

.news-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #828282;
  padding: 16px;
}

.share-btn {
  border: none;
  background: transparent;
  color: #36b453;
  cursor: pointer;
}

.news-content {
  padding: 0 16px 16px;
}

.title {
  font-size: 18px;
  margin-bottom: 16px;
}

.description {
  line-height: 1.7;
  color: #4f4f4f;
}

.description :deep(img) {
  max-width: 100%;
}
</style>
